import React, { useState, useId } from "react";

interface SegmentedInputProps {
  length: number;
  onComplete: (value: string) => void;
  defaultValue?: string;
  value?: string;
  type?: "text" | "number";
  disabled?: boolean;
}

const SegmentedInput: React.FC<SegmentedInputProps> = ({
  length,
  onComplete,
  defaultValue = "",
  value,
  type = "text",
  disabled = false,
}) => {
  // Generate ID unik untuk setiap instance komponen ini
  const uniqueId = useId();

  const [values, setValues] = useState<string[]>(
    Array.from({ length }, (_, i) => (value !== undefined ? value : defaultValue)[i] || ""),
  );

  React.useEffect(() => {
    const val = value !== undefined ? value : defaultValue;
    setValues((prevValues) => {
      if (val !== prevValues.join("")) {
        return Array.from({ length }, (_, i) => val[i] || "");
      }
      return prevValues;
    });
  }, [value, defaultValue, length]);

  const handleChange = (value: string, index: number) => {
    if (disabled) return;

    const newValue = value.slice(-1);
    const newValues = [...values];
    newValues[index] = newValue;
    setValues(newValues);

    // Pindah fokus ke input selanjutnya menggunakan uniqueId
    if (newValue && index < length - 1) {
      const nextInput = document.getElementById(
        `segmented-${uniqueId}-${index + 1}`,
      );
      nextInput?.focus();
    }

    const combinedValue = newValues.join("");
    onComplete(combinedValue);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (disabled) return;

    // Pindah fokus ke input sebelumnya menggunakan uniqueId saat Backspace
    if (e.key === "Backspace" && !values[index] && index > 0) {
      const prevInput = document.getElementById(
        `segmented-${uniqueId}-${index - 1}`,
      );
      prevInput?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (disabled) return;

    const pastedData = e.clipboardData
      .getData("text/plain")
      .trim()
      .slice(0, length);

    if (pastedData) {
      const newValues = [...values];
      for (let i = 0; i < pastedData.length; i++) {
        if (i < length) newValues[i] = pastedData[i];
      }
      setValues(newValues);
      onComplete(newValues.join(""));

      const lastIndex = Math.min(pastedData.length, length) - 1;
      const targetInput = document.getElementById(
        `segmented-${uniqueId}-${lastIndex}`,
      );
      targetInput?.focus();
    }
  };

  return (
    <div className="grid grid-cols-8 items-center w-full gap-2">
      {values.map((val, index) => (
        <input
          key={index}
          id={`segmented-${uniqueId}-${index}`}
          type={type}
          maxLength={1}
          value={val}
          disabled={disabled}
          onChange={(e) => handleChange(e.target.value, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={handlePaste}
          className="h-10 w-10 flex-shrink-0 p-0 text-center font-semibold outline-none appearance-none transition-all duration-200
            /* --- RESPONSIVE TYPOGRAPHY & ROUNDED --- */
            text-lg sm:text-xl md:text-2xl lg:text-3xl
            rounded-md sm:rounded-lg md:rounded-xl
            /* --- LIGHT MODE --- */
            bg-slate-50 border border-slate-200 text-slate-900 
            focus:border-primary focus:ring-2 focus:ring-primary/20 focus:bg-white
            /* --- DARK MODE --- */
            dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 
            dark:focus:border-primary dark:focus:ring-primary/30 dark:focus:bg-slate-800
            /* --- DISABLED STATE --- */
            disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100 dark:disabled:bg-slate-800 dark:disabled:text-slate-500
            /* Menghilangkan panah atas/bawah pada input type number */
            [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
      ))}
    </div>
  );
};

export default SegmentedInput;
